import DispatchContent from "../../features/planning/dailyDispatch/DispatchContent";
import JobReport from "../../features/planning/dailyDispatch/DailyJobReport/JobReport";
import DispatchScopeActionMenu from "../../features/planning/dailyDispatch/shared/DispatchScopeActionMenu";
import { useEffect } from "react";

import TopBar from "../../features/planning/dailyDispatch/TopBar";

import WorkerSelectModal from "../../features/planning/shared/WorkerSelectModal";
import { Alert } from "../../components/ui";
import { ConfirmModal } from "../../components/ui";

import useDispatchCtx, { DispatchContext } from "../../context/domain/planning/DispatchContext";
import useCaptureCtx, { CaptureContext } from "../../context/domain/planning/CaptureContext";
import useWeeklyCtx, { WeeklyContext, useWeeklyContext } from "../../context/global/WeeklyContext";
import {
  formatShortMonthDay,
  getDateInLocalTZFromDateStr,
} from "../../utils/datetime/core";

import { handleKeyPress } from "../../utils/dom";

/**
 * Inner component that uses both Weekly and Dispatch contexts.
 * This must be inside the WeeklyContext provider.
 */
const DispatchInner = () => {
  const dispatchCtx = useDispatchCtx();
  const { jobs, showWeekends } = useWeeklyContext();
  const {
    isWorkerModalOpen,
    closeWorkerModal,
    modalProps,
    isDispatchCapturing,
    isJobReportCapturing,
    setCopyScopeOrigin,
    setCopyScopesDate,
    workerUnskipConflictModal,
    confirmWorkerUnskipConflictModal,
    cancelWorkerUnskipConflictModal,
    scopeActionMenu,
  } = dispatchCtx;
  // Capture context is used for setting the state and vars of the capture. The capture
  // has different requirements than the main dispatch so it has its own context.
  const captureCtx = useCaptureCtx();

  useEffect(() => {
    const cleanup = handleKeyPress("Escape", () => {
      setCopyScopeOrigin(null);
      setCopyScopesDate(null);
    });

    return cleanup;
  }, [setCopyScopeOrigin, setCopyScopesDate]);

  return (
    <DispatchContext.Provider value={dispatchCtx}>
      <div
        className={`font-inter min-h-screen w-full ${showWeekends ? "min-w-dispatchWeekends" : "min-w-dispatch"} cursor-default caret-transparent bg-gray-50`}
      >
        {/* Main content of the dispatch - no fixed height so sticky (NavBar, TopBar, day headers) stay in view when scrolling */}
        <div className="flex flex-col font-inter min-h-screen w-full cursor-default caret-transparent bg-gray-50">
          <div className="z-20 sticky top-12">
            <TopBar />
          </div>
          <DispatchContent />
        </div>
        {/* Copy of dispatch content used for capturing */}
        <CaptureContext.Provider value={captureCtx}>
          {/* Only mount capture components during a capture request to save performance */}
          {isDispatchCapturing && (
            <DispatchContent className="hidden dispatch-content-to-capture" />
          )}
          {isJobReportCapturing &&
            jobs &&
            jobs.map((job) => (
              <JobReport
                key={job.id}
                className={`${job.number} jobreport-content-to-capture`}
                job={job}
              />
            ))}
        </CaptureContext.Provider>
        {/* Global dispatch modals reside here at top level */}
        {/* Please note we generally do not use global modals for simplicity, 
      encapsulation and to avoid coupling. Worker modal is the only current exception:
      -- Accessed from many places, doesn't use local context, state preservation */}
        {isWorkerModalOpen && (
          <WorkerSelectModal
            {...modalProps}
            isOpen={isWorkerModalOpen}
            onClose={closeWorkerModal}
          />
        )}
        {scopeActionMenu ? (
          <div
            className="fixed z-[10030] h-0 w-0"
            style={{
              left: scopeActionMenu.anchorRect?.right ?? 0,
              top: scopeActionMenu.anchorRect?.top ?? 0,
            }}
          >
            <DispatchScopeActionMenu
              scope={scopeActionMenu.scope}
              date={scopeActionMenu.date}
              align="right"
              showButton={false}
              openKey={scopeActionMenu.openKey}
              disableCopy={scopeActionMenu.disableCopy}
            />
          </div>
        ) : null}
        <ConfirmModal
          isOpen={workerUnskipConflictModal != null}
          title="Worker Conflict Detected"
          buttonText="Confirm"
          onConfirm={confirmWorkerUnskipConflictModal}
          onClose={cancelWorkerUnskipConflictModal}
        >
          <div className="mx-auto max-w-md space-y-2 text-left">
            {(workerUnskipConflictModal ?? []).map(({ dateKey, conflictNames }) => (
              <Alert
                key={dateKey}
                type="warning"
                message={`${formatShortMonthDay(
                  getDateInLocalTZFromDateStr(dateKey)
                )}: ${conflictNames.join(", ")}`}
              />
            ))}
            <div className="pt-2 text-center text-gray-600">
              This unskipped scope will be assigned an updated crew without workers
              already assigned or marked off work for this day.
            </div>
          </div>
        </ConfirmModal>
      </div>
    </DispatchContext.Provider>
  );
};

/**
 * Entry point for the Daily Dispatch page app frontend.
 *
 * @component
 */
const DailyDispatch = () => {
  const weeklyCtx = useWeeklyCtx();

  return (
    <WeeklyContext.Provider value={weeklyCtx}>
      <DispatchInner />
    </WeeklyContext.Provider>
  );
};

export default DailyDispatch;
